import { splitEncoding } from '@contentful/live-preview';

import type { UserLocation } from '@/data-store/redux/slices/userLocationSlice';
import type { SectionStateConstraintCF } from '@/types';

export function sanitizeList(list?: string[]): string[] | undefined {
  // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
  return list && list.map((value) => splitEncoding(value).cleaned);
}

function sanitizeZipCode(zipCode: string | null): string | null {
  if (!zipCode || zipCode.length < 5) return null;
  return zipCode.substring(0, 5);
}

// userLocation: is the user state, city, or zip code, like [CA, HI, WA], [Beverly Hills, Honolulu], [90210, 10020]...
function determineDataMatches(
  list: string[],
  userLocation: string,
  constraintType: boolean
): boolean {
  const matches = list.includes(userLocation);
  return constraintType ? matches : !matches;
}

function getStateCityMatches(states: string[], cities: string[]): string[] {
  return states.flatMap((state) => cities.map((city) => `${city}, ${state}`));
}

// Checks if the state constraint applies to the user location
// stateConstraint: is the constraint state to be checked
// userLocation: is the user state and zip code, like CA, HI, WA, etc.
export function checkStateConstraint({
  stateConstraint,
  userLocation,
}: {
  stateConstraint: SectionStateConstraintCF;
  userLocation: UserLocation;
}): boolean {
  // get the user location
  const userState = userLocation.state;
  const userCity = userLocation.city;
  const userZipCode = sanitizeZipCode(userLocation.zipCode);

  if (!userState && !userCity && !userZipCode) {
    return false;
  }

  // get the constraint zip code list
  const constraintZipCode = sanitizeList(
    stateConstraint?.fields?.zipCodeList?.split(',')
  );

  // get the constraint state list
  const constraintState = sanitizeList(stateConstraint?.fields?.states);

  // get the constraint city list
  const constraintCity = sanitizeList(stateConstraint?.fields?.cities);

  // get the zip code list if exists
  const zipCodeList =
    constraintZipCode && constraintZipCode.length > 0 && constraintZipCode;

  // get the state list if exists
  const stateList =
    constraintState && constraintState.length > 0 && constraintState;

  // get the city list if exists
  const cityList =
    constraintCity && constraintCity.length > 0 && constraintCity;

  // get the constraint type
  const constraintType = stateConstraint?.fields?.constraintType;

  // If both states and cities are configured, match city-state combinations.
  if (userState && userCity && stateList && cityList) {
    return determineDataMatches(
      getStateCityMatches(stateList, cityList),
      `${userCity}, ${userState}`,
      constraintType
    );
  }

  // If only states are configured, use state matching.
  if (userState && stateList && !cityList) {
    return determineDataMatches(stateList, userState, constraintType);
  }

  // If only zip codes are configured, use zip code matching.
  if (userZipCode && zipCodeList && !stateList && !cityList) {
    return determineDataMatches(zipCodeList, userZipCode, constraintType);
  }

  // if none applies, return false
  return false;
}
